import Delete from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowsProp,
  GridToolbar
} from '@mui/x-data-grid-pro'
import { FC, useCallback, useMemo, useState } from 'react'

import { formatMarginInDataGrid } from '../../components/CannedJobs/CannedJobParts'
import {
  getMarginOfCannedPart,
  getPartsMatrixOfCannedPart,
  getPostTaxPrice,
  getRetailPrice,
  getRetailPricePerUnitOfCannedPart
} from '../../components/CannedJobs/customPartsCalculation'
import { CannedJobPartsDialogUI } from '../../components/CannedJobs/PartsDialogUI/CannedJobPartsDialogUI'
import { useAddCustomPartsDialog } from '../../components/CannedJobs/useAddCustomPartsDialog/useAddCustomPartsDialog'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetPartsMatrices } from '../../hooks/useGetPartsMatrices/useGetPartsMatrices'
import { useGetVendorEntries } from '../../hooks/useGetVendorEntries/useGetVendorEntries'
import { LabelsPartsInfo } from '../../models/Labels'
import {
  useDeleteCannedJobCustomPartMutation,
  useListCannedPartsQuery
} from '../../redux/api/cannedJobApi'
import { useGetTaxesAndFeesQuery } from '../../redux/api/shopMatrixApi'
import { NONE_ID } from '../../utils/convertEmptyStringToUndefined/convertEmptyStringToUndefined'
import { formatCurrencyInDataGrid } from '../../utils/formatCurrencyInDataGrid'

export type PartTypes = 'Custom' | 'Lookup' | 'Inventory' | 'MotorFluids'

interface Props {}

export const InventoryProductTable: FC<Props> = () => {
  const [deleteProps, setDeleteProps] = useState<
    { id: string; type: PartTypes } | undefined
  >()
  const [deleteCustomPart] = useDeleteCannedJobCustomPartMutation()

  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: products, isLoading } = useListCannedPartsQuery({
    authorization: accessToken,
    shop: shopId
  })
  const { allPartsMatrices } = useGetPartsMatrices([])
  const { setCustomProps: setAddCustomPartsProps, customPartsDialogProps } =
    useAddCustomPartsDialog({
      submitCallback: undefined,
      cannedJobId: undefined,
      counterSaleId: undefined
    })

  const { data: taxesAndFees } = useGetTaxesAndFeesQuery({
    authorization: accessToken,
    shop: shopId
  })

  const columns = useMemo(
    () => [
      {
        field: 'partNumber',
        headerName: LabelsPartsInfo.partNumber,
        flex: 1
      },
      { field: 'name', headerName: 'Parts ' + LabelsPartsInfo.name, flex: 1 },
      {
        field: 'quantity',
        headerName: LabelsPartsInfo.quantity,
        flex: 1,
        type: 'number'
      },
      {
        field: 'unitOfMeasure',
        headerName: LabelsPartsInfo.unitOfMeasure,
        flex: 1
      },
      {
        field: 'costPerUnit',
        headerName: LabelsPartsInfo.costPerUnit,
        flex: 1,
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'partsMatrix',
        headerName: LabelsPartsInfo.partsMatrix,
        flex: 1
      },
      {
        field: 'margin',
        headerName: LabelsPartsInfo.margin,
        flex: 1,
        type: 'number',
        valueFormatter: formatMarginInDataGrid
      },
      {
        field: 'retailPricePerUnit',
        headerName: LabelsPartsInfo.retailPricePerUnit,
        flex: 1,
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'retailPrice',
        headerName: LabelsPartsInfo.retailPrice,
        flex: 1,
        valueGetter: getRetailPrice,
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'postTaxPrice',
        headerName: LabelsPartsInfo.postTax,
        flex: 1,
        valueGetter: (params) => getPostTaxPrice(params, taxesAndFees),
        type: 'number',
        valueFormatter: formatCurrencyInDataGrid
      },
      {
        field: 'vendorName',
        headerName: LabelsPartsInfo.vendorName,
        flex: 1
      },
      {
        field: 'description',
        headerName: LabelsPartsInfo.description,
        flex: 1
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 2,
        getActions: (params: GridRenderCellParams) => [
          <GridActionsCellItem
            key="Edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => {
              setAddCustomPartsProps({
                existingEntry: products?.find((entry) => entry.id === params.id)
              })
            }}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            key="Delete"
            onClick={() =>
              setDeleteProps({
                id: params.id! as string,
                type: params.row.type
              })
            }
          />
        ]
      }
    ],
    [products, setAddCustomPartsProps, taxesAndFees]
  )

  const { shopVendorsAllMap } = useGetVendorEntries()

  const rows = useMemo(() => {
    const customPartRows: GridRowsProp = (products || []).map((data) => {
      const margin = getMarginOfCannedPart(data, allPartsMatrices)
      const retailPricePerUnit = getRetailPricePerUnitOfCannedPart(
        data,
        allPartsMatrices
      )
      const foundMatrix = getPartsMatrixOfCannedPart(data, allPartsMatrices)
      const partsMatrix = foundMatrix
        ? foundMatrix.info!.description!
        : retailPricePerUnit
        ? NONE_ID
        : ''

      return {
        id: data.id!,
        ...data.info!,
        partsMatrix,
        margin,
        retailPricePerUnit,
        vendorName:
          data.info?.vendorId !== undefined
            ? shopVendorsAllMap.get(data?.info?.vendorId)?.info?.name
            : data?.info?.supplierName
      }
    })
    return customPartRows
  }, [allPartsMatrices, products, shopVendorsAllMap])

  const handleDelete = useCallback(async () => {
    if (!deleteProps) {
      return
    }
    deleteCustomPart({
      authorization: accessToken,
      shop: shopId!,
      customPart: deleteProps.id
    })
    setDeleteProps(undefined)
  }, [accessToken, deleteCustomPart, deleteProps, shopId])
  return (
    <>
      {customPartsDialogProps && (
        <CannedJobPartsDialogUI {...customPartsDialogProps} />
      )}
      <Dialog open={!!deleteProps} onClose={() => setDeleteProps(undefined)}>
        <DialogTitle>Delete Part Item</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this product?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteProps(undefined)}>Cancel</Button>
          <Button onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <DataGridPro
        disableRowSelectionOnClick
        slots={{
          toolbar: GridToolbar
        }}
        loading={isLoading}
        density="compact"
        columns={columns}
        rows={rows}
        sx={{ border: '0px' }}
      />
    </>
  )
}
